import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, MessageCircle, ChevronDown, ChevronUp, Crown, Flag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useSubscription } from "@/hooks/use-subscription";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { z } from "zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const reviewSchema = z.object({
  rating: z.number().min(1).max(5),
  title: z.string().trim().max(150).optional(),
  body: z.string().trim().min(20, "Review must be at least 20 characters").max(2000),
  relationship: z.string().optional(),
});

interface ReviewSectionProps {
  nhSlug: string;
  nursingHomeName: string;
}

function StarRating({ rating, onRate, interactive = false }: { rating: number; onRate?: (r: number) => void; interactive?: boolean }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-5 w-5 ${interactive ? "cursor-pointer" : ""} ${
            star <= (hover || rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
          }`}
          onClick={() => interactive && onRate?.(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
        />
      ))}
    </div>
  );
}

export function ReviewSection({ nhSlug, nursingHomeName }: ReviewSectionProps) {
  const { user } = useAuth();
  const { subscribed: isPremium } = useSubscription();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 0, title: "", body: "", relationship: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch reviews with responses
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["reviews", nhSlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*, review_responses(*)")
        .eq("nursing_home_id", nhSlug)
        .eq("is_published", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Check if user already reviewed
  const { data: existingReview } = useQuery({
    queryKey: ["user-review", nhSlug, user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("reviews")
        .select("id")
        .eq("nursing_home_id", nhSlug)
        .eq("user_id", user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const submitReview = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Must be logged in");
      const result = reviewSchema.safeParse(form);
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.errors.forEach((err) => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        setErrors(fieldErrors);
        throw new Error("Validation failed");
      }

      // Get profile for reviewer name
      const { data: profile } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", user.id)
        .single();

      const reviewerName = profile
        ? `${profile.first_name || ""} ${(profile.last_name || "").charAt(0)}.`.trim()
        : "Anonymous";

      const { error } = await supabase.from("reviews").insert({
        nursing_home_id: nhSlug,
        user_id: user.id,
        reviewer_name: reviewerName,
        rating: result.data.rating,
        title: result.data.title || null,
        body: result.data.body,
        relationship: result.data.relationship || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", nhSlug] });
      queryClient.invalidateQueries({ queryKey: ["user-review", nhSlug] });
      setShowForm(false);
      setForm({ rating: 0, title: "", body: "", relationship: "" });
      toast.success("Review submitted! Thank you for your feedback.");
    },
    onError: (error: any) => {
      if (error.message !== "Validation failed") {
        toast.error("Failed to submit review");
      }
    },
  });

  const avgRating = reviews.length > 0
    ? reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / reviews.length
    : 0;

  return (
    <Card>
      <CardHeader className="pb-3 bg-grandpal-beige/20">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-grandpal-green-dark flex items-center gap-2">
            <Star className="h-5 w-5" />
            Reviews
            {reviews.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({reviews.length}) · {avgRating.toFixed(1)} avg
              </span>
            )}
          </CardTitle>
          {user && !existingReview && isPremium && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(!showForm)}
            >
              Write a Review
            </Button>
          )}
          {user && !existingReview && !isPremium && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/premium")}
              className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
            >
              <Crown className="h-3.5 w-3.5 mr-1" />
              Write a Review
            </Button>
          )}
          {!user && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/auth/signup")}
            >
              Sign in to Review
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        {/* Review Form */}
        {showForm && (
          <Card className="border-dashed">
            <CardContent className="p-4 space-y-3">
              <div className="space-y-1.5">
                <Label>Your Rating *</Label>
                <StarRating rating={form.rating} onRate={(r) => setForm({ ...form, rating: r })} interactive />
                {errors.rating && <p className="text-xs text-destructive">{errors.rating}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Your Relationship</Label>
                <Select value={form.relationship} onValueChange={(v) => setForm({ ...form, relationship: v })}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="family_member">Family Member</SelectItem>
                    <SelectItem value="resident">Resident</SelectItem>
                    <SelectItem value="visitor">Visitor</SelectItem>
                    <SelectItem value="professional">Healthcare Professional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Brief summary of your experience"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Your Review *</Label>
                <Textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Share your experience..."
                  rows={4}
                  className={errors.body ? "border-destructive" : ""}
                />
                {errors.body && <p className="text-xs text-destructive">{errors.body}</p>}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => submitReview.mutate()} disabled={submitReview.isPending || form.rating === 0}>
                  {submitReview.isPending ? "Submitting..." : "Submit Review"}
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews List */}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading reviews...</p>
        ) : reviews.length === 0 ? (
          <div className="text-center py-6">
            <Star className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-muted-foreground">No reviews yet.</p>
            {!user && (
              <p className="text-xs text-muted-foreground mt-1">
                Log in to be the first to leave a review.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review: any) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReviewCard({ review }: { review: any }) {
  const [expanded, setExpanded] = useState(false);
  const [reported, setReported] = useState(false);
  const response = review.review_responses?.[0];
  const isLong = review.body.length > 300;

  const relationshipLabels: Record<string, string> = {
    family_member: "Family Member",
    resident: "Resident",
    visitor: "Visitor",
    professional: "Healthcare Professional",
  };

  const handleReport = () => {
    setReported(true);
    toast.success("Review reported. Our team will review it shortly.");
  };

  return (
    <div className="border-b last:border-0 pb-4 last:pb-0">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <StarRating rating={review.rating} />
            {review.title && <span className="font-medium text-sm">{review.title}</span>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {review.reviewer_name}
            {review.relationship && ` · ${relationshipLabels[review.relationship] || review.relationship}`}
            {" · "}
            {format(new Date(review.created_at), "MMM d, yyyy")}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-destructive h-7 px-2"
          onClick={handleReport}
          disabled={reported}
          title="Report this review"
        >
          <Flag className={`h-3.5 w-3.5 ${reported ? "fill-muted-foreground" : ""}`} />
        </Button>
      </div>
      <p className="text-sm mt-2 whitespace-pre-line">
        {isLong && !expanded ? review.body.slice(0, 300) + "..." : review.body}
      </p>
      {isLong && (
        <Button variant="link" size="sm" className="p-0 h-auto text-xs" onClick={() => setExpanded(!expanded)}>
          {expanded ? <><ChevronUp className="h-3 w-3 mr-1" /> Show less</> : <><ChevronDown className="h-3 w-3 mr-1" /> Read more</>}
        </Button>
      )}

      {/* Owner Response */}
      {response && (
        <div className="mt-3 ml-4 pl-3 border-l-2 border-grandpal-green/30 bg-grandpal-beige/10 rounded-r p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <MessageCircle className="h-3.5 w-3.5 text-grandpal-green" />
            <span className="text-xs font-medium text-grandpal-green-dark">
              Response from {response.responder_name}
            </span>
            <span className="text-xs text-muted-foreground">
              · {format(new Date(response.created_at), "MMM d, yyyy")}
            </span>
          </div>
          <p className="text-sm whitespace-pre-line">{response.body}</p>
        </div>
      )}
    </div>
  );
}
